// A thin, defensive wrapper around the browser Notifications API. Every
// function here is safe to call even when the API doesn't exist at all
// (older Firefox on some platforms, most in-app webviews) — the widget's
// existing badge/sound/title system is the fallback (see widget.ts), and
// nothing in this module may ever throw into a caller that isn't expecting
// it.

export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export type NotificationPermissionState = 'default' | 'granted' | 'denied';

export function getNotificationPermission(): NotificationPermissionState {
  if (!isNotificationSupported()) {
    return 'denied';
  }

  return Notification.permission;
}

// Only ever actually prompts when permission is still 'default' — the
// browser itself is a no-op (immediate resolve, no UI) when it's already
// 'granted' or 'denied', so callers don't need to guard against repeat
// calls beyond not spamming them pointlessly.
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!isNotificationSupported()) {
    return 'denied';
  }

  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

// A small branded icon, generated once at runtime rather than shipped as a
// binary asset — the widget bundle stays a single self-contained file with
// zero external requests (see docs/WidgetInstallation.md). Falls back to
// undefined (the OS's own default notification glyph) if canvas is
// unavailable for any reason, exactly the "استخدم شعار الدعم الافتراضي"
// fallback the spec calls for.
let cachedIconDataUrl: string | null | undefined;

function buildNotificationIcon(): string | undefined {
  if (cachedIconDataUrl !== undefined) {
    return cachedIconDataUrl ?? undefined;
  }

  try {
    const size = 192;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      cachedIconDataUrl = null;
      return undefined;
    }

    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, '#F5921F');
    gradient.addColorStop(1, '#E96D11');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();

    // A simple chat-bubble glyph, matching the launcher's own icon shape.
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = size * 0.075;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    const r = size * 0.27;
    const cx = size / 2;
    const cy = size * 0.46;
    ctx.arc(cx, cy, r, Math.PI * 0.15, Math.PI * 1.85);
    ctx.moveTo(cx - r * 0.55, cy + r * 0.82);
    ctx.lineTo(cx - r * 0.95, cy + r * 1.55);
    ctx.lineTo(cx - r * 0.05, cy + r * 0.98);
    ctx.stroke();

    cachedIconDataUrl = canvas.toDataURL('image/png');
    return cachedIconDataUrl;
  } catch {
    cachedIconDataUrl = null;
    return undefined;
  }
}

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  onClick: () => void;
}

// Fixed tag: a later call while an earlier notification is still showing
// REPLACES it at the OS level instead of stacking a second entry — the
// caller (widget.ts) is responsible for only calling this once per
// debounced batch of arrivals, this is just the belt-and-suspenders
// backstop against ever showing two.
const NOTIFICATION_TAG = 'flix-support-message';

// Returns the Notification instance so the caller can close it early (e.g.
// the customer read the messages some other way before it was dismissed).
// Never throws — a permission race (revoked between the check and this
// call) or any other platform quirk is swallowed, since a failed
// notification must never be treated as a delivery failure for the
// message itself.
export function showBrowserNotification(options: BrowserNotificationOptions): Notification | null {
  if (getNotificationPermission() !== 'granted') {
    return null;
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: buildNotificationIcon(),
      tag: NOTIFICATION_TAG,
      // Explicitly NOT setting silent: true — the OS's own notification
      // sound (if any) is meant to be the one sound the customer hears for
      // this arrival; widget.ts skips its own chime whenever a browser
      // notification is being shown, precisely to avoid two.
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      options.onClick();
    };

    return notification;
  } catch {
    return null;
  }
}
